import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { DoorOpen, MailCheck, MailX, SquareCheck, Wrench, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { RunStatusBadge } from '@/components/ui/RunStatusBadge';
import { StatCard } from '@/components/ui/StatCard';
import { systemPrimaryName } from '@/components/ui/SystemName';
import { formatDateTime } from '@/lib/format';
import {
  isSessionConflict,
  isSessionOpen,
  sessionConflictDetail,
  useCloseMaintenanceSession,
  useMaintenanceSession,
  useOpenMaintenanceSession,
  useUpdateMaintenanceSession,
  type MaintenanceSession,
  type MaintenanceSessionRun,
} from '@/hooks/useMaintenanceSession';
import { useSystems } from '@/hooks/useSystems';

/**
 * Wartungsfenster-Karte auf der Kundenseite.
 *
 * Ein Fenster pro Kunde gibt den Kundenmails den Takt - deshalb steht die
 * Karte oben auf der Detailseite und nicht in einem Tab. Wichtigste Zeile ist
 * der Mailstatus: man muss auf einen Blick sehen, ob der Kunde schon etwas
 * bekommen hat.
 *
 * Auf/Zu geht IMMER von Hand, auch ganz ohne Läufe. "Ohne Kundenmail"
 * (silent) ist an beiden Aktionen verfügbar.
 */

interface Props {
  customerId: string;
}

const uhrzeitFormatter = new Intl.DateTimeFormat('de-AT', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Vienna',
});

function formatUhrzeit(value: string | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return uhrzeitFormatter.format(d);
}

function laeufeLabel(n: number): string {
  return n === 1 ? '1 Lauf' : `${n} Läufe`;
}

/** Backend liefert entweder eine Anzahl oder die Namensliste - beides rendern. */
function systemeLabel(systeme: MaintenanceSessionRun['systeme']): string {
  const n = Array.isArray(systeme) ? systeme.length : systeme;
  if (typeof n !== 'number' || !Number.isFinite(n)) return '';
  return n === 1 ? '1 System' : `${n} Systeme`;
}

export function MaintenanceSessionCard({ customerId }: Props): JSX.Element {
  const query = useMaintenanceSession(customerId);
  const [dialog, setDialog] = useState<'open' | 'close' | null>(null);

  const session = query.data?.session ?? null;
  const runs = query.data?.runs ?? [];
  const counts = query.data?.counts;
  const offen = isSessionOpen(session);

  return (
    <Card
      data-testid="maintenance-session-card"
      title={
        <span className="inline-flex items-center gap-2">
          <Wrench className="size-4" aria-hidden="true" />
          Wartungsfenster
        </span>
      }
      description="Ein Fenster pro Kunde — es taktet die Kundenmails, nicht der einzelne Lauf."
      actions={
        query.data ? (
          offen ? (
            <Button
              leftIcon={<SquareCheck className="size-4" />}
              onClick={() => setDialog('close')}
              data-testid="session-close"
            >
              Wartung abschließen
            </Button>
          ) : (
            <Button
              leftIcon={<DoorOpen className="size-4" />}
              onClick={() => setDialog('open')}
              data-testid="session-open"
            >
              Wartung öffnen
            </Button>
          )
        ) : null
      }
    >
      {query.isLoading && <p className="text-sm text-muted">Lädt ...</p>}

      {query.isError && (
        <div className="flex flex-col gap-2">
          <ErrorAlert error={query.error} />
          <div>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Erneut laden
            </Button>
          </div>
        </div>
      )}

      {query.data && (
        <div className="flex flex-col gap-4">
          {offen && session ? (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Badge tone="warning" data-testid="session-status">
                  Wartung
                </Badge>
                <p data-testid="session-open-since" className="text-sm">
                  Wartung läuft seit{' '}
                  <time
                    dateTime={session.opened_at}
                    title={formatDateTime(session.opened_at)}
                    className="font-medium"
                  >
                    {formatUhrzeit(session.opened_at)}
                  </time>{' '}
                  <span className="text-muted">
                    (<RelativeTime value={session.opened_at} />)
                  </span>
                </p>
                {session.auto_opened && (
                  <span className="text-xs text-muted">automatisch aufgegangen</span>
                )}
              </div>

              {counts && (
                <div
                  className={`grid grid-cols-2 gap-2 ${
                    counts.blockiert > 0 ? 'sm:grid-cols-3 lg:grid-cols-5' : 'sm:grid-cols-4'
                  }`}
                >
                  <StatCard label="Läufe" value={counts.gesamt} testId="session-count-gesamt" />
                  <StatCard
                    label="laufend"
                    value={counts.laufend}
                    tone={counts.laufend > 0 ? 'primary' : 'neutral'}
                    testId="session-count-laufend"
                  />
                  <StatCard
                    label="fehlgeschlagen"
                    value={counts.fehlgeschlagen}
                    tone={counts.fehlgeschlagen > 0 ? 'danger' : 'neutral'}
                    testId="session-count-fehlgeschlagen"
                  />
                  {/* Nur wenn es sie gibt: eine Dauer-"0 blockiert" wäre im
                      Normalfall Rauschen. Gelb statt rot - kein Fehlschlag,
                      sondern nicht stattgefunden. Der helpText trägt die
                      Zahl, auf die es ankommt: die Historienzahl fällt nie
                      wieder auf 0, `unerledigt` schon. */}
                  {counts.blockiert > 0 && (
                    <StatCard
                      label="blockiert"
                      value={counts.blockiert}
                      tone={counts.unerledigt > 0 ? 'warning' : 'neutral'}
                      helpText={
                        counts.unerledigt > 0 ? `${counts.unerledigt} noch offen` : 'nachgeholt'
                      }
                      testId="session-count-blockiert"
                    />
                  )}
                  <StatCard
                    label="erfolgreich"
                    value={counts.erfolgreich}
                    tone={counts.erfolgreich > 0 ? 'success' : 'neutral'}
                    testId="session-count-erfolgreich"
                  />
                </div>
              )}

              <MailStatus session={session} />

              <NotizBlock customerId={customerId} session={session} />

              <ManuelleSystemeBlock customerId={customerId} session={session} />

              <RunList runs={runs} />
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Badge tone="neutral" data-testid="session-status">
                  Keine Wartung offen
                </Badge>
                <p className="text-sm text-muted">
                  Ohne offenes Fenster geht keine Kundenmail raus. Öffnen und Schließen geht
                  jederzeit von Hand — auch ohne laufende Wartung.
                </p>
              </div>

              {session && (
                <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                  <p className="text-sm">
                    Letztes Fenster:{' '}
                    <span className="text-muted">
                      {formatDateTime(session.opened_at)} bis {formatDateTime(session.closed_at)}
                    </span>
                    {session.had_failures && (
                      <Badge tone="warning" className="ml-2">
                        mit Fehlern geschlossen
                      </Badge>
                    )}
                  </p>
                  <MailStatus session={session} />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {dialog === 'open' && (
        <OpenSessionDialog customerId={customerId} onDone={() => setDialog(null)} />
      )}
      {dialog === 'close' && (
        <CloseSessionDialog
          customerId={customerId}
          gesamt={counts?.gesamt ?? runs.length}
          laufend={counts?.laufend ?? 0}
          fehlgeschlagen={counts?.fehlgeschlagen ?? 0}
          unerledigt={counts?.unerledigt ?? 0}
          onDone={() => setDialog(null)}
        />
      )}
    </Card>
  );
}

/**
 * Der Blick, der nach dem 18-Mails-Abend zählt: hat der Kunde schon etwas
 * bekommen - und wann.
 */
function MailStatus({ session }: { session: MaintenanceSession }): JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-2 p-3"
      data-testid="session-mail-status"
    >
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted">Kundenmails</h3>
      <ul className="mt-2 flex flex-col gap-1.5 text-sm">
        <MailRow label="Wartung gestartet" sentAt={session.started_mail_sent_at} />
        <MailRow label="Wartung abgeschlossen" sentAt={session.finished_mail_sent_at} />
      </ul>
    </div>
  );
}

function MailRow({ label, sentAt }: { label: string; sentAt: string | null }): JSX.Element {
  const gesendet = Boolean(sentAt);
  return (
    <li className="flex flex-wrap items-center gap-2">
      {gesendet ? (
        <MailCheck className="size-4 shrink-0 text-success" aria-hidden="true" />
      ) : (
        <MailX className="size-4 shrink-0 text-muted" aria-hidden="true" />
      )}
      <span className="font-medium">{label}:</span>
      {gesendet ? (
        <span className="text-success">gesendet {formatDateTime(sentAt)}</span>
      ) : (
        <span className="text-muted">noch keine Mail verschickt</span>
      )}
    </li>
  );
}

function RunList({ runs }: { runs: MaintenanceSessionRun[] }): JSX.Element {
  if (runs.length === 0) {
    return <p className="text-sm text-muted">Noch kein Lauf in diesem Fenster.</p>;
  }
  return (
    <ul
      className="divide-y divide-border rounded-md border border-border"
      data-testid="session-runs"
    >
      {runs.map((run) => {
        const systeme = systemeLabel(run.systeme);
        return (
          <li
            key={run.id}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
          >
            <Link
              to="/runs/$id"
              params={{ id: run.id }}
              className="truncate text-primary hover:underline"
            >
              {run.routine_display_name}
            </Link>
            <span className="flex items-center gap-2">
              {systeme && <span className="text-xs text-muted">{systeme}</span>}
              <RunStatusBadge status={run.status} />
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function SilentToggle({
  checked,
  onChange,
  label,
  hint,
  testId,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
  testId: string;
}): JSX.Element {
  return (
    <label
      htmlFor={testId}
      className="grid grid-cols-[auto_1fr] items-start gap-x-2 gap-y-0.5 rounded-md border border-border p-3 text-sm"
    >
      <input
        id={testId}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 accent-primary"
        data-testid={testId}
      />
      <span className="font-medium">{label}</span>
      <span className="col-start-2 text-xs text-muted">{hint}</span>
    </label>
  );
}

function OpenSessionDialog({
  customerId,
  onDone,
}: {
  customerId: string;
  onDone: () => void;
}): JSX.Element {
  const [silent, setSilent] = useState(false);
  const [notes, setNotes] = useState('');
  const mut = useOpenMaintenanceSession();

  const submit = (): void => {
    const input: { customerId: string; silent: boolean; notes?: string } = {
      customerId,
      silent,
    };
    const trimmed = notes.trim();
    if (trimmed) input.notes = trimmed;
    mut.mutate(input, { onSuccess: onDone });
  };

  return (
    <Dialog
      open
      onClose={onDone}
      title="Wartung öffnen"
      description="Solange das Fenster offen ist, läuft alles darunter unter einer Wartung."
      footer={
        <>
          <Button variant="ghost" onClick={onDone} disabled={mut.isPending}>
            Abbrechen
          </Button>
          <Button loading={mut.isPending} onClick={submit} data-testid="session-confirm-open">
            Wartung öffnen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm" data-testid="session-open-mail-hint">
          {silent
            ? 'Es geht KEINE Mail raus. Der Kunde erfährt nichts vom Start.'
            : 'Der Kunde bekommt genau eine Mail: "Wartung gestartet".'}
        </p>

        <SilentToggle
          checked={silent}
          onChange={setSilent}
          label="Ohne Kundenmail öffnen"
          hint="Fenster geht auf, es wird nichts verschickt."
          testId="session-open-silent"
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="session-open-notes" className="text-sm font-medium">
            Notiz (optional)
          </label>
          <textarea
            id="session-open-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            placeholder="z.B. Wartungsabend August, Server + VMs"
          />
          <p className="text-xs text-muted" data-testid="session-open-notes-hint">
            Steht als Hinweis-Zeile in den Kundenmails „Wartung gestartet“ und „Wartung
            abgeschlossen“ — also so schreiben, wie der Kunde es lesen soll.
          </p>
        </div>

        {mut.isError && <ErrorAlert error={mut.error} />}
      </div>
    </Dialog>
  );
}

/**
 * Zwei Gründe halten das Schließen auf, und sie sind NICHT dasselbe:
 *
 *   fehlgeschlagen  Der Lauf ist auf dem Kundensystem gescheitert. Dort ist
 *                   etwas passiert, vielleicht halb - rot.
 *   unerledigt      Der Lauf hat das Kundensystem NIE ERREICHT (blockiert,
 *                   kein Zugang) und die Arbeit wurde nicht nachgeholt. Dort
 *                   ist nichts passiert - gelb, und die Abhilfe ist eine
 *                   völlig andere: Zugang herstellen, Lauf wiederholen.
 *
 * Vor dem Befund vom 13.08.2026 hing die Schranke allein an `fehlgeschlagen`.
 * Ein Fenster mit ausschließlich blockierten Läufen zeigte einen blauen
 * "Wartung abschliessen"-Knopf ohne jeden Hinweis - und schickte dem Kunden
 * "Wartung abgeschlossen" für eine Wartung, die nie stattgefunden hat.
 *
 * `unerledigt` (Wire: runs_blocked_offen) ist bewusst NICHT `blockiert`: die
 * Historienzahl fällt nie wieder auf 0 und würde das Fenster für immer
 * klemmen.
 */
function CloseSessionDialog({
  customerId,
  gesamt,
  laufend,
  fehlgeschlagen,
  unerledigt,
  onDone,
}: {
  customerId: string;
  gesamt: number;
  laufend: number;
  fehlgeschlagen: number;
  unerledigt: number;
  onDone: () => void;
}): JSX.Element {
  const [silent, setSilent] = useState(false);
  const [anmerkung, setAnmerkung] = useState('');
  const [ack, setAck] = useState(false);
  // Sticky: einmal 409 gesehen = die Bestätigung bleibt sichtbar, auch
  // während der Wiederholung (mut.error ist dann kurz null).
  const [forceRequired, setForceRequired] = useState(false);
  const [conflictDetail, setConflictDetail] = useState<string | null>(null);
  const ackRef = useRef<HTMLInputElement>(null);
  const mut = useCloseMaintenanceSession();

  // Beides ist schon vor dem Absenden bekannt -> gar nicht erst in den 409
  // laufen lassen, sondern sofort erklären.
  const zeigeBlockiert = unerledigt > 0;
  // Der rote Kasten ist zugleich der Rückfall für einen 409, den die
  // Zähler nicht erklären (Race): dann steht wenigstens der Text des
  // Backends da.
  const zeigeFehler = fehlgeschlagen > 0 || (forceRequired && !zeigeBlockiert);
  const nurBlockiert = zeigeBlockiert && !zeigeFehler;
  const brauchtBestaetigung = zeigeFehler || zeigeBlockiert;

  // Der Haken darf nicht "trotz Fehlern" sagen, wenn gar nichts fehlgeschlagen
  // ist - genau diese Verwechslung soll die Karte ja abstellen.
  const ackGrund = nurBlockiert
    ? 'Ja, ich schließe das Fenster bewusst, obwohl die Wartung dort nicht stattgefunden hat'
    : zeigeBlockiert
      ? 'Ja, ich schließe das Fenster bewusst trotz Fehlern und nicht erreichter Systeme'
      : 'Ja, ich schließe das Fenster bewusst trotz Fehlern';

  useEffect(() => {
    if (forceRequired) ackRef.current?.focus();
  }, [forceRequired]);

  const submit = (): void => {
    const input: { customerId: string; silent: boolean; force?: boolean; anmerkung?: string } = {
      customerId,
      silent,
    };
    if (ack) input.force = true;
    if (anmerkung.trim()) input.anmerkung = anmerkung.trim();
    mut.mutate(input, {
      onSuccess: onDone,
      onError: (err) => {
        if (!isSessionConflict(err)) return;
        setForceRequired(true);
        setConflictDetail(sessionConflictDetail(err));
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onDone}
      title="Wartung abschließen"
      description={`${laeufeLabel(gesamt)} in diesem Fenster.`}
      footer={
        <>
          <Button variant="ghost" onClick={onDone} disabled={mut.isPending}>
            Abbrechen
          </Button>
          <Button
            variant={brauchtBestaetigung ? 'danger' : 'primary'}
            loading={mut.isPending}
            disabled={brauchtBestaetigung && !ack}
            onClick={submit}
            data-testid="session-confirm-close"
          >
            {brauchtBestaetigung ? 'Trotzdem abschließen' : 'Wartung abschließen'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm" data-testid="session-close-mail-hint">
          {silent
            ? 'Es geht KEINE Mail raus. Das Fenster wird nur geschlossen.'
            : 'Der Kunde bekommt genau eine Mail: "Wartung abgeschlossen".'}
        </p>

        {laufend > 0 && (
          <p className="text-sm text-muted">
            Achtung: {laufend === 1 ? '1 Lauf läuft' : `${laufend} Läufe laufen`} noch.
          </p>
        )}

        {brauchtBestaetigung && (
          <div className="flex flex-col gap-3">
            {/* Gelb, nicht rot, und bewusst VOR dem Fehlerkasten: "nie
                erreicht" ist der Zustand, den Colin am leichtesten übersieht
                und der die andere Abhilfe verlangt. */}
            {zeigeBlockiert && (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
                data-testid="session-close-blocked-warning"
              >
                <p className="font-medium text-warning">
                  {unerledigt === 1
                    ? '1 Lauf hat das Kundensystem nie erreicht.'
                    : `${unerledigt} Läufe haben das Kundensystem nie erreicht.`}
                </p>
                <p>
                  Kein Zugang, kein Task gelaufen: die Wartung hat dort{' '}
                  <strong>nicht stattgefunden</strong>. Das ist kein Fehlschlag — am Kundensystem
                  wurde nichts verändert, die Arbeit ist schlicht offen.
                </p>
                <p data-testid="session-close-blocked-abhilfe">
                  <strong>Statt abzuschließen:</strong> Zugang herstellen — Yubikey stecken und
                  berühren, VPN prüfen, Bitwarden entsperren (bw unlock), SSH-Schlüssel hinterlegen
                  — und die betroffenen Läufe erneut starten. Sobald die Systeme gewartet sind,
                  verschwindet diese Warnung von selbst und das Fenster schließt ohne Bestätigung.
                </p>
                {nurBlockiert && conflictDetail && (
                  <p className="text-warning/90">{conflictDetail}</p>
                )}
                <p>
                  {silent
                    ? 'Schließt du jetzt, steht die Wartung als erledigt da, obwohl sie nie stattgefunden hat.'
                    : 'Schließt du jetzt, geht die Mail "Wartung abgeschlossen" an den Kunden raus — für eine Wartung, die nie stattgefunden hat.'}
                </p>
              </div>
            )}

            {zeigeFehler && (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-md border border-danger/40 bg-danger/5 p-3 text-sm"
                data-testid="session-close-warning"
              >
                <p className="font-medium text-danger">
                  {fehlgeschlagen > 0
                    ? `${fehlgeschlagen} von ${gesamt} Läufen ist fehlgeschlagen.`
                    : 'Das Fenster hält noch Fehler fest.'}
                </p>
                {conflictDetail && <p className="text-danger/90">{conflictDetail}</p>}
                <p>
                  Schließt du jetzt, geht die Mail{' '}
                  <strong>&quot;Wartung abgeschlossen&quot;</strong> an den Kunden raus, obwohl
                  etwas schiefgegangen ist. Normalerweise bleibt das Fenster offen, bis der Fehler
                  repariert ist.
                </p>
              </div>
            )}

            {/* EIN Haken für beide Gründe - zwei wären zwei Wege, versehentlich
                doch abzuschließen. Der Text nennt den Grund, der wirklich
                vorliegt. */}
            <label className="flex items-start gap-2 text-sm">
              <input
                ref={ackRef}
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className={`mt-0.5 size-4 ${nurBlockiert ? 'accent-warning' : 'accent-danger'}`}
                data-testid="session-force-ack"
              />
              <span>
                {ackGrund}
                {!silent && ' und der Kunde bekommt die Abschluss-Mail'}.
              </span>
            </label>
          </div>
        )}

        {/* Anmerkung (16.09.2026): "es gab Komplikationen wie beim Video-Server" -
            eigener Absatz in der Abschlussmail. Ohne Mail nur im Verlauf. */}
        <div className="flex flex-col gap-1">
          <label htmlFor="session-close-anmerkung" className="text-sm font-medium">
            Anmerkung an den Kunden (optional)
          </label>
          <textarea
            id="session-close-anmerkung"
            rows={3}
            value={anmerkung}
            onChange={(e) => setAnmerkung(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            placeholder="z. B. Beim Videoserver gab es Komplikationen, er läuft seit 17:40 wieder."
            data-testid="session-close-anmerkung"
          />
          <p className="text-xs text-muted">
            {silent
              ? 'Steht nur im Wartungsverlauf, es geht keine Mail raus.'
              : 'Steht als eigener Absatz „Anmerkung“ in der Abschlussmail.'}
          </p>
        </div>

        <SilentToggle
          checked={silent}
          onChange={setSilent}
          label="Ohne Kundenmail abschließen"
          hint="Fenster geht zu, es wird nichts verschickt."
          testId="session-close-silent"
        />

        {mut.isError && !isSessionConflict(mut.error) && <ErrorAlert error={mut.error} />}
      </div>
    </Dialog>
  );
}

/**
 * Notiz des Fensters. Seit 16.09.2026 geht sie als Hinweis-Zeile in die
 * Kundenmails "Wartung gestartet" (wenn beim Öffnen gesetzt) und "Wartung
 * abgeschlossen" - deshalb am offenen Fenster editierbar und mit dem
 * Hinweis, dass der Kunde sie liest. Leer speichern löscht sie.
 */
function NotizBlock({
  customerId,
  session,
}: {
  customerId: string;
  session: MaintenanceSession;
}): JSX.Element {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(session.notes ?? '');
  const mut = useUpdateMaintenanceSession();

  useEffect(() => {
    if (!bearbeiten) setText(session.notes ?? '');
  }, [session.notes, bearbeiten]);

  const speichern = (): void => {
    mut.mutate(
      { customerId, sessionId: session.id, notes: text.trim() },
      { onSuccess: () => setBearbeiten(false) }
    );
  };

  return (
    <div
      className="flex flex-col gap-1 rounded-md border border-border p-3"
      data-testid="session-notiz"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted">
          Notiz für den Kunden
        </span>
        {!bearbeiten && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBearbeiten(true)}
            data-testid="session-notiz-bearbeiten"
          >
            {session.notes ? 'Ändern' : 'Notiz hinzufügen'}
          </Button>
        )}
      </div>
      {bearbeiten ? (
        <div className="flex flex-col gap-2">
          <textarea
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Notiz für den Kunden"
            data-testid="session-notiz-text"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={mut.isPending}
              onClick={speichern}
              data-testid="session-notiz-speichern"
            >
              Speichern
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setBearbeiten(false)}
              disabled={mut.isPending}
            >
              Abbrechen
            </Button>
          </div>
          {mut.isError && <ErrorAlert error={mut.error} />}
        </div>
      ) : session.notes ? (
        <p className="whitespace-pre-wrap text-sm" data-testid="session-notiz-text-anzeige">
          {session.notes}
        </p>
      ) : (
        <p className="text-sm text-muted">Keine Notiz.</p>
      )}
      <p className="text-xs text-muted">
        Geht als Hinweis-Zeile in die Kundenmails „Wartung gestartet“ und „Wartung abgeschlossen“.
      </p>
    </div>
  );
}

/**
 * Von Hand gewartete Systeme am OFFENEN Fenster (16.09.2026, Hasselbach):
 * Colin wartet hv01 und video02 selbst, ohne Lauf. Hier eingetragen zählen
 * sie in der Abschlussmail als betreute Systeme und bekommen beim Schließen
 * den Wartungsstand - ein zweites Fenster per Nacherfassung entfällt.
 * Jede Änderung geht sofort an den Server (kompletter Stand, kein Delta).
 */
function ManuelleSystemeBlock({
  customerId,
  session,
}: {
  customerId: string;
  session: MaintenanceSession;
}): JSX.Element {
  const systemeQuery = useSystems({ customerId, limit: 500 });
  const mut = useUpdateMaintenanceSession();
  const ids = session.manuelle_systeme ?? [];
  const alle = systemeQuery.data?.data ?? [];

  const nameVon = (id: string): string => {
    const s = alle.find((x) => x.id === id);
    return s ? systemPrimaryName(s) : `${id.slice(0, 8)}…`;
  };
  const optionen = alle
    .filter((s) => !ids.includes(s.id))
    .map((s) => ({
      value: s.id,
      label: systemPrimaryName(s),
      hint: s.display_name && s.display_name !== s.hostname ? s.hostname : undefined,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));

  const setze = (neu: string[]): void => {
    mut.mutate({ customerId, sessionId: session.id, manuelleSysteme: neu });
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border p-3"
      data-testid="session-manuelle-systeme"
    >
      <span className="text-xs font-medium uppercase tracking-wider text-muted">
        Von Hand gewartet
        {ids.length > 0 && <span className="ml-1 normal-case tracking-normal">({ids.length})</span>}
      </span>
      {ids.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Von Hand gewartete Systeme">
          {ids.map((id) => (
            <li
              key={id}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-2 px-2 py-0.5 text-sm"
              data-testid="session-manuelles-system"
            >
              <span>{nameVon(id)}</span>
              <button
                type="button"
                aria-label={`${nameVon(id)} entfernen`}
                onClick={() => setze(ids.filter((x) => x !== id))}
                disabled={mut.isPending}
                className="text-muted hover:text-text disabled:opacity-50"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Noch kein System eingetragen.</p>
      )}
      <Combobox
        label="System von Hand hinzufügen"
        hideLabel
        ariaLabel="System von Hand hinzufügen"
        value={null}
        onChange={(v) => {
          if (v) setze([...ids, v]);
        }}
        options={optionen}
        placeholder={
          systemeQuery.isLoading ? 'Systeme werden geladen …' : 'System von Hand hinzufügen …'
        }
        disabled={mut.isPending || optionen.length === 0}
        emptyText="Alle Systeme dieses Kunden sind schon eingetragen."
      />
      <p className="text-xs text-muted">
        Zählen in der Abschlussmail als betreute Systeme und bekommen beim Schließen den
        Wartungsstand.
      </p>
      {mut.isError && <ErrorAlert error={mut.error} />}
    </div>
  );
}
